/**
 * Project Name: Brainsie eCommerce v4
 * Component: Icon Card
 * Description: Card component with icon at top, heading, and description text
 * Author: Brainsie Team
 * Date Created: October 30, 2025
 */

import React from "react";
import { Box, Card, CardContent, Typography } from "@mui/material";

export default function IconCard({
  component,
  section,
  darkMode,
  theme,
  gridIndex,
}) {
  // Get component settings
  const componentSettings = component.Component_Settings
    ? JSON.parse(component.Component_Settings)
    : {};

  // Default settings
  const iconSize = componentSettings.iconSize || 64;
  const cardPadding = componentSettings.cardPadding || 24;
  const textAlign = componentSettings.textAlign || "center";

  // Get icon from Media_Files array (first image) - similar to SimpleCard logic
  const getIconFromMediaFiles = () => {
    try {
      let mediaFile;
      if (Array.isArray(component.Media_Files)) {
        mediaFile = component.Media_Files[0];
      } else if (typeof component.Media_Files === "string") {
        const parsed = JSON.parse(component.Media_Files);
        mediaFile = Array.isArray(parsed) ? parsed[0] : parsed;
      } else if (component.Media_Files) {
        mediaFile = component.Media_Files;
      }
      return mediaFile || null;
    } catch (e) {
      console.log("Error parsing Media_Files:", e);
      return null;
    }
  };

  const iconFile = getIconFromMediaFiles();
  const hasIcon = iconFile !== null;

  // Debug logging
  console.log("🎯 ICON CARD DEBUG:", {
    componentName: component.Component_Name,
    primaryText: component.Primary_Text,
    secondaryText: component.Secondary_Text,
    descriptiveText: component.Descriptive_Text,
    mediaFiles: component.Media_Files,
    mediaFilesType: typeof component.Media_Files,
    iconFile: iconFile,
    hasIcon: hasIcon,
    fullImageUrl: hasIcon
      ? iconFile.startsWith("http")
        ? iconFile
        : `${process.env.BASEURL}${iconFile}`
      : null,
  });

  return (
    <Card
      sx={{
        width: "100%",
        maxWidth: 350,
        height: "auto",
        borderRadius: "12px",
        backgroundColor: darkMode
          ? theme.palette.grey[800]
          : theme.palette.background.paper,
        border: `1px solid ${
          darkMode ? theme.palette.grey[700] : theme.palette.divider
        }`,
        boxShadow: darkMode
          ? "0 2px 8px rgba(0,0,0,0.3)"
          : "0 2px 8px rgba(0,0,0,0.1)",
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        "&:hover": {
          transform: "translateY(-4px)",
          boxShadow: darkMode
            ? "0 6px 20px rgba(0,0,0,0.4)"
            : "0 6px 20px rgba(0,0,0,0.15)",
        },
      }}
    >
      <CardContent
        sx={{
          p: `${cardPadding}px !important`,
          textAlign: textAlign,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          minHeight: "240px",
          justifyContent: "flex-start",
        }}
      >
        {/* Icon/Image from Media_Files */}
        {hasIcon && (
          <Box
            component="img"
            src={
              iconFile.startsWith("http")
                ? iconFile
                : `${process.env.BASEURL}${iconFile}`
            }
            alt={component.Primary_Text || "Icon"}
            sx={{
              width: iconSize,
              height: iconSize,
              borderRadius: "8px",
              mb: 2,
              objectFit: "contain",
              backgroundColor: "transparent",
            }}
            onError={(e) => {
              console.log("Image failed to load:", iconFile);
              e.target.style.display = "none";
            }}
          />
        )}

        {/* Fallback icon if no media */}
        {!hasIcon && (
          <Box
            sx={{
              width: iconSize,
              height: iconSize,
              borderRadius: "8px",
              mb: 2,
              backgroundColor: darkMode
                ? "rgba(255, 255, 255, 0.1)"
                : "rgba(0, 0, 0, 0.1)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: darkMode
                ? theme.palette.grey[400]
                : theme.palette.grey[600],
              fontSize: "24px",
            }}
          >
            📋
          </Box>
        )}

        {/* Primary Text (Main Heading) */}
        {component.Primary_Text && component.Primary_Text !== "NA" && (
          <Typography
            variant="h6"
            component="h3"
            sx={{
              fontWeight: 600,
              fontSize: { xs: "1.1rem", md: "1.25rem" },
              lineHeight: 1.2,
              mb:
                component.Secondary_Text && component.Secondary_Text !== "NA"
                  ? 1
                  : 1.5,
              color: darkMode
                ? theme.palette.common.white
                : theme.palette.text.primary,
              textAlign: textAlign,
            }}
          >
            {component.Primary_Text}
          </Typography>
        )}

        {/* Secondary Text (Subheading) */}
        {component.Secondary_Text && component.Secondary_Text !== "NA" && (
          <Typography
            variant="subtitle2"
            sx={{
              fontSize: "0.9rem",
              lineHeight: 1.4,
              mb: 1.5,
              color: darkMode
                ? theme.palette.grey[400]
                : theme.palette.text.secondary,
              fontWeight: 500,
              textAlign: textAlign,
            }}
          >
            {component.Secondary_Text}
          </Typography>
        )}

        {/* Descriptive Text */}
        {component.Descriptive_Text && component.Descriptive_Text !== "NA" && (
          <Typography
            variant="body2"
            sx={{
              fontSize: "0.95rem",
              lineHeight: 1.6,
              color: darkMode
                ? theme.palette.grey[300]
                : theme.palette.text.secondary,
              opacity: 0.9,
              textAlign: textAlign,
              flex: 1,
            }}
          >
            {component.Descriptive_Text}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}
